/**
 * Utility functions for handling environment variables
 */

/**
 * Gets an environment variable (build-time only).
 * @param {string} key - The environment variable key
 * @returns {string} - The environment variable value
 */
export const getEnvVar = (key) => {
  return process.env[key] ?? '';
};

/**
 * Checks if the application is running in production mode
 * @returns {boolean} - True if in production mode
 */
export const isProduction = () => {
  const nodeEnv = getEnvVar('NODE_ENV');
  const squareEnv = getEnvVar('SQUARE_ENVIRONMENT');
  
  return nodeEnv?.toLowerCase() === 'production' || squareEnv === 'production';
};

/**
 * Gets the Square location ID based on the current environment
 * @returns {string} - The Square location ID
 */
export const getSquareLocationId = () => {
  return isProduction()
    ? getEnvVar('REACT_APP_SQ_PRODUCTION_LOCATION_ID')
    : getEnvVar('REACT_APP_SQ_LOCATION_ID_TPH');
};

/**
 * Gets the Square application ID based on the current environment
 * @returns {string} - The Square application ID
 */
export const getSquareAppId = () => {
  return isProduction()
    ? getEnvVar('REACT_APP_SQ_PRODUCTION_APP_ID')
    : getEnvVar('REACT_APP_SQ_APPLICATION_ID');
};

/**
 * Admin-configured support contact, populated by SiteSettingsContext once
 * site settings load. Takes priority over env vars so the admin panel can
 * change support contact info without a redeploy.
 */
let supportContactOverride = {
  businessName: null,
  supportEmail: null,
  supportPhone: null,
  address: null,
};

export const setSupportContactOverride = (contact) => {
  supportContactOverride = {
    businessName: contact?.businessName || null,
    supportEmail: contact?.supportEmail || null,
    supportPhone: contact?.supportPhone || null,
    address: contact?.address || null,
  };
};

export const getBusinessName = () => (
  supportContactOverride.businessName || getEnvVar('REACT_APP_BUSINESS_NAME') || 'Truck Parking Hub'
);

export const getBusinessAddressLines = () => {
  const address = supportContactOverride.address || {};
  const line1 = address.line1 || getEnvVar('REACT_APP_BUSINESS_ADDRESS_LINE1');
  const line2 = address.line2 || getEnvVar('REACT_APP_BUSINESS_ADDRESS_LINE2');
  const locality = [
    address.city || getEnvVar('REACT_APP_BUSINESS_ADDRESS_CITY'),
    address.state || getEnvVar('REACT_APP_BUSINESS_ADDRESS_STATE'),
  ].filter(Boolean).join(', ');
  const postalCode = address.postalCode || getEnvVar('REACT_APP_BUSINESS_ADDRESS_POSTAL_CODE');
  return [line1, line2, [locality, postalCode].filter(Boolean).join(' ')].filter(Boolean);
};

/**
 * Gets the support email address
 * @returns {string} - The support email address
 */
export const getSupportEmail = () => {
  return supportContactOverride.supportEmail
    || getEnvVar('REACT_APP_SUPPORT_EMAIL')
    || '';
};

/**
 * Gets the support phone number
 * @returns {string} - The support phone number
 */
export const getSupportPhone = () => {
  return supportContactOverride.supportPhone
    || getEnvVar('REACT_APP_SUPPORT_PHONE')
    || '';
};
